import { Outlet, useLocation, Link } from "react-router-dom";
import { useEffect } from "react";
import { Footer } from "@/components/shared/footer";
import { Navigation as Header } from "@/components/shared/navigation";
import {
    Breadcrumb,
    BreadcrumbItem,
    BreadcrumbLink,
    BreadcrumbList,
    BreadcrumbPage,
    BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface RouteConfig {
    title: string;
    subtitle: string;
    breadcrumbs: { label: string; path?: string }[];
}

const routeConfigs: Record<string, RouteConfig> = {
    "/": {
        title: "Video Troubleshooter",
        subtitle:
            "Test your browser's video playback and resolve any issues quickly.",
        breadcrumbs: [{ label: "Home" }],
    },
    "/troubleshooting": {
        title: "Basic Troubleshooting",
        subtitle:
            "Follow the steps below to diagnose and fix video playback issues.",
        breadcrumbs: [{ label: "Home", path: "/" }, { label: "Basic Troubleshooting" }],
    },
    "/contact-support": {
        title: "Contact Support",
        subtitle:
            "Reach out to our team for personalized help with your video issues.",
        breadcrumbs: [
            { label: "Home", path: "/" },
            { label: "Basic Troubleshooting", path: "/troubleshooting" },
            { label: "Contact Support" },
        ],
    },
};

export default function Layout() {
    const location = useLocation();
    const config = routeConfigs[location.pathname] ?? routeConfigs["/"];

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [location.pathname]);

    return (
        <div className="min-h-screen bg-background">
            <Header />

            {/* ML-style Hero Section */}
            <section className="relative w-full bg-cream-100 pt-24 pb-6 sm:pt-28 sm:pb-8 md:pt-28 md:pb-10">
                <div className="container max-w-7xl mx-auto px-4 sm:px-6">
                    {/* Breadcrumbs */}
                    {config.breadcrumbs.length > 1 && (
                        <Breadcrumb className="mb-4">
                            <BreadcrumbList>
                                {config.breadcrumbs.map((crumb, index) => {
                                    const isLast = index === config.breadcrumbs.length - 1;
                                    return (
                                        <BreadcrumbItem key={crumb.label}>
                                            {index > 0 && <BreadcrumbSeparator />}
                                            {isLast ? (
                                                <BreadcrumbPage>{crumb.label}</BreadcrumbPage>
                                            ) : (
                                                <BreadcrumbLink asChild>
                                                    <Link to={crumb.path!}>{crumb.label}</Link>
                                                </BreadcrumbLink>
                                            )}
                                        </BreadcrumbItem>
                                    );
                                })}
                            </BreadcrumbList>
                        </Breadcrumb>
                    )}

                    <div className="flex flex-col gap-2 items-start">
                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight tracking-tight text-black">
                            {config.title}
                        </h1>
                        <p className="text-base sm:text-lg text-text-muted max-w-2xl">
                            {config.subtitle}
                        </p>
                    </div>
                </div>
            </section>

            <main className="w-full bg-white">
                <div className="container max-w-7xl mx-auto px-4 sm:px-6 py-8 md:py-12">
                    <Outlet />
                </div>
            </main>

            <Footer />
        </div>
    );
}
